import { HomeNav } from "@/components/home/HomeApple";
import { isPublicPath } from "@/lib/publicRoutes";
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, Heart, User, LogOut, Shield, Briefcase, Home,
  CreditCard, Settings, Users, MessageCircle, Search, LayoutDashboard,
  BarChart3, FileText, Building2, UserCheck, TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "./NotificationBell";
import { useNavMenu, type RoleKey } from "@/hooks/useNavMenu";
import { NavIcon } from "@/components/NavIcon";
import { NavProfileAvatar } from "@/components/NavProfileAvatar";
import { SiteBrand } from "@/components/SiteBrand";

type UserRole = "caregiver" | "client" | "b2b" | "sales" | "admin" | null;

const LegacyNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isB2B, setIsB2B] = useState(false);
  const [isSales, setIsSales] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // Derived: focus mode hides public menu for B2B and Sales
  const isFocusMode = isB2B || isSales;

  // Determine effective role for menu rendering
  const effectiveRole: UserRole = isAdmin
    ? "admin"
    : isB2B
      ? "b2b"
      : isSales
        ? "sales"
        : (userType as UserRole);

  // Map effective role → menu role key (sales/admin fall back to guest-like menu)
  const menuRoleKey: RoleKey =
    effectiveRole === "caregiver" ? "caregiver"
    : effectiveRole === "client" ? "client"
    : effectiveRole === "b2b" ? "b2b"
    : "guest";

  const { data: desktopMenuItems } = useNavMenu("desktop_navbar", menuRoleKey);
  const { data: drawerMenuItems } = useNavMenu("mobile_drawer", menuRoleKey);

  const unreadLabel = unreadMessageCount > 0
    ? `Berichten, ${unreadMessageCount > 9 ? "meer dan 9" : unreadMessageCount} ongelezen ${unreadMessageCount === 1 ? "gesprek" : "gesprekken"}`
    : "Berichten";

  // Fetch unread message count
  useEffect(() => {
    if (!user) {
      setUnreadMessageCount(0);
      return;
    }

    const fetchUnreadCount = async () => {
      // Aantal gesprekken met minstens één ongelezen bericht (server-side geteld)
      const { data, error } = await (supabase as any).rpc("get_unread_conversation_count");
      if (!error) setUnreadMessageCount(Number(data) || 0);
    };

    fetchUnreadCount();

    const channel = supabase
      .channel(`navbar-messages-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_messages" },
        () => fetchUnreadCount()
      )
      .subscribe();

    const onRead = () => fetchUnreadCount();
    window.addEventListener("chat-read", onRead);

    // Vangnet als realtime niet werkt
    const poll = window.setInterval(fetchUnreadCount, 30000);

    return () => { window.clearInterval(poll); window.removeEventListener("chat-read", onRead); supabase.removeChannel(channel); };
  }, [user]);

  // Check roles
  useEffect(() => {
    const checkRoles = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsB2B(false);
        setIsSales(false);
        setUserType(null);
        return;
      }

      setUserType(user.user_metadata?.user_type || null);

      try {
        const [adminRes, b2bRes, salesRes] = await Promise.all([
          supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
          supabase.rpc("has_role", { _user_id: user.id, _role: "b2b" }),
          supabase.rpc("has_role", { _user_id: user.id, _role: "sales" }),
        ]);
        setIsAdmin(adminRes.data === true);
        setIsB2B(b2bRes.data === true);
        setIsSales(salesRes.data === true);
      } catch {
        setIsAdmin(false);
        setIsB2B(false);
        setIsSales(false);
      }
    };

    checkRoles();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // ─── Universal public menu ───
  const publicLinks = [
    { to: "/zoek-zorgverlener", label: "Zorgverleners" },
    { to: "/vacatures", label: "Hulpvragen" },
    { to: "/hoe-werkt-het", label: "Uitleg" },
    { to: "/abonnementen", label: "Prijzen" },
    { to: "/zorgbedrijven", label: "Bedrijven" },
  ];

  // ─── B2B focus menu ───
  const b2bLinks = [
    { to: "/b2b-dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/b2b-dashboard?tab=caregivers", label: "Kandidaten", icon: UserCheck },
    { to: "/b2b-dashboard?tab=vacatures", label: "Vacature Beheer", icon: Briefcase },
    { to: "/b2b-dashboard?tab=clients", label: "Team", icon: Users },
  ];

  // ─── Sales focus menu ───
  const salesLinks = [
    { to: "/sales-dashboard", label: "Sales Dashboard", icon: LayoutDashboard },
    { to: "/sales-dashboard?tab=leads", label: "Leads", icon: Users },
    { to: "/sales-dashboard?tab=conversions", label: "Conversies", icon: TrendingUp },
    { to: "/sales-dashboard?tab=reports", label: "Rapportage", icon: BarChart3 },
  ];

  const isActiveLink = (to: string) => location.pathname === to || location.pathname + location.search === to;

  // ─── Desktop Nav Links ───
  const DesktopNav = () => {
    if (isFocusMode) {
      const links = isB2B ? b2bLinks : salesLinks;
      return (
        <>
          {links.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                isActiveLink(to) ? "text-primary" : "text-foreground hover:text-primary"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          ))}
        </>
      );
    }

    // Use admin-managed menu items when available; fallback to publicLinks.
    const items = (desktopMenuItems && desktopMenuItems.length > 0)
      ? desktopMenuItems.map((i) => ({ to: i.path, label: i.label }))
      : (userType === "caregiver"
          ? publicLinks.filter((l) => l.to !== "/zoek-zorgverlener")
          : publicLinks);

    return (
      <>
        {items.filter(({ to }) => to !== "/zorgsoorten").map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            className="text-foreground hover:text-primary transition-colors font-semibold"
          >
            {label}
          </Link>
        ))}
        {user && isAdmin && (
          <Link to="/zzadmin" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
            Admin
          </Link>
        )}
      </>
    );
  };

  // ─── Icon bar (favorites, messages, notifications) ───
  const IconBar = () => {
    if (!user) return null;

    const favTarget = "/vacatures?tab=favorieten";

    return (
      <>
        <Button variant="ghost" size="icon" onClick={() => navigate(favTarget)} title="Mijn favorieten">
          <Heart className="w-5 h-5" />
        </Button>
         <Button variant="ghost" size="icon" className="relative" aria-label={unreadLabel} onClick={() => navigate("/mijn-praktijk/berichten")}>
          <MessageCircle className="w-5 h-5" />
          {unreadMessageCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 bg-destructive text-destructive-foreground text-[10px] font-semibold rounded-full min-w-5 px-1 flex items-center justify-center">
              {unreadMessageCount > 9 ? "9+" : unreadMessageCount}
            </span>
          )}
        </Button>
        <NotificationBell />
      </>
    );
  };

  // ─── Account dropdown per role ───
  const AccountDropdown = ({ trigger }: { trigger?: React.ReactNode }) => {
    if (!user) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger ?? (
            <Button variant="outline" size="sm">
              <User className="w-4 h-4 mr-2" />
              {effectiveRole === "caregiver" ? "Mijn Praktijk" : "Mijn Account"}
            </Button>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52">
          {/* ── Admin ── */}
          {effectiveRole === "admin" && (
            <>
              <DropdownMenuItem onClick={() => navigate("/zzadmin")}>
                <Shield className="w-4 h-4 mr-2" />
                Admin Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/b2b-dashboard")}>
                <Briefcase className="w-4 h-4 mr-2" />
                Recruiter Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/instellingen")}>
                <Settings className="w-4 h-4 mr-2" />
                Instellingen
              </DropdownMenuItem>
            </>
          )}

          {/* ── Caregiver ── */}
          {effectiveRole === "caregiver" && (
            <>
              <DropdownMenuItem onClick={() => navigate("/mijn-praktijk")}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Mijn Praktijk
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/mijn-praktijk/profiel")}>
                <User className="w-4 h-4 mr-2" />
                Mijn Profiel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/mijn-praktijk/berichten")}>
                <MessageCircle className="w-4 h-4 mr-2" />
                Berichten
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/mijn-praktijk/facturatie")}>
                <FileText className="w-4 h-4 mr-2" />
                Boekhouding
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/mijn-praktijk/abonnement")}>
                <CreditCard className="w-4 h-4 mr-2" />
                Abonnement
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/mijn-praktijk/ambassadeur")}>
                <Users className="w-4 h-4 mr-2" />
                Ambassadeur
              </DropdownMenuItem>
            </>
          )}

          {/* ── Client ── */}
          {effectiveRole === "client" && (
            <>
               <DropdownMenuItem onClick={() => navigate("/client-account")}>
                <User className="w-4 h-4 mr-2" />
                Mijn Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/client-dashboard")}>
                <Home className="w-4 h-4 mr-2" />
                Mijn Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/client-dashboard")}>
                <Briefcase className="w-4 h-4 mr-2" />
                Mijn Hulpvragen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/instellingen")}>
                <Settings className="w-4 h-4 mr-2" />
                Instellingen
              </DropdownMenuItem>
            </>
          )}

          {/* ── B2B ── */}
          {effectiveRole === "b2b" && (
            <>
              <DropdownMenuItem onClick={() => navigate("/instellingen")}>
                <Building2 className="w-4 h-4 mr-2" />
                Bedrijfsinstellingen
              </DropdownMenuItem>
            </>
          )}

          {/* ── Sales ── */}
          {effectiveRole === "sales" && (
            <>
              <DropdownMenuItem onClick={() => navigate("/instellingen")}>
                <Settings className="w-4 h-4 mr-2" />
                Instellingen
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="w-4 h-4 mr-2" />
            Uitloggen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Logo home target — caregivers go to /vacatures
  const homeTarget = effectiveRole === "caregiver" ? "/vacatures" : "/";

  // ─── Mobile links ───
  const MobileNav = () => {
    if (isFocusMode) {
      const links = isB2B ? b2bLinks : salesLinks;
      return (
        <>
          {links.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isActiveLink(to)
                  ? "text-primary bg-primary/10 font-semibold"
                  : "text-foreground hover:text-primary hover:bg-muted"
              }`}
              onClick={() => setIsOpen(false)}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          ))}
        </>
      );
    }

    // For caregivers, the public links are rendered grouped under "Ontdekken" inside MobileUserLinks.
    if (effectiveRole === "caregiver") return null;

    return (
      <>
        {publicLinks.filter(({ to }) => to !== "/zorgsoorten").map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            className="px-4 py-2 text-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors font-semibold"
            onClick={() => setIsOpen(false)}
          >
            {label}
          </Link>
        ))}
      </>
    );
  };

  const MobileUserLinks = () => {
    if (!user) return null;

    const sectionLabel = "px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70";
    const itemClass = "flex items-center gap-2 px-4 py-2 text-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors";

    // Use admin-managed drawer items when available (caregiver/client/b2b).
    if (drawerMenuItems && drawerMenuItems.length > 0) {
      // Group by section, preserving order
      const grouped: { section: string | null; items: typeof drawerMenuItems }[] = [];
      for (const item of drawerMenuItems) {
        const last = grouped[grouped.length - 1];
        if (last && last.section === item.section) {
          last.items.push(item);
        } else {
          grouped.push({ section: item.section, items: [item] });
        }
      }

      return (
        <>
          {grouped.map((group, gi) => (
            <div key={gi}>
              {group.section && <div className={sectionLabel}>{group.section}</div>}
              {group.items.filter((item) => item.path !== "/zorgsoorten").map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={itemClass}
                  onClick={() => setIsOpen(false)}
                >
                  <NavIcon name={item.icon} className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </>
      );
    }

    // Fallback for admin/sales (no DB menus configured) — keep legacy links.
    return (
      <>
        {effectiveRole === "admin" && (
          <>
            <Link to="/zzadmin" className={itemClass} onClick={() => setIsOpen(false)}>
              Admin Dashboard
            </Link>
            <Link to="/b2b-dashboard" className={itemClass} onClick={() => setIsOpen(false)}>
              Recruiter Dashboard
            </Link>
          </>
        )}
        <Link to="/mijn-praktijk/berichten" className={itemClass} onClick={() => setIsOpen(false)}>
          Berichten
        </Link>
        <Link to="/instellingen" className={itemClass} onClick={() => setIsOpen(false)}>
          Instellingen
        </Link>
      </>
    );
  };

  return (
    <>
      {/* ─── Top Bar ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={homeTarget} className="flex items-center gap-2">
              <SiteBrand className="text-xl" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <DesktopNav />
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  <IconBar />
                  <AccountDropdown />
                  {effectiveRole === "client" && (
                    <Link to="/plaats-advertentie">
                      <Button size="sm">Plaats Hulpvraag</Button>
                    </Link>
                  )}
                  <AccountDropdown
                    trigger={
                      <button type="button" aria-label="Accountmenu" className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <NavProfileAvatar preferCompany={effectiveRole === "b2b"} />
                      </button>
                    }
                  />
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" size="sm">Inloggen</Button>
                  </Link>
                  <Link to="/login">
                    <Button size="sm">Registreren</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile: icons + hamburger */}
            <div className="flex md:hidden items-center gap-1">
              {user && (
                <>
                  <Button variant="ghost" size="icon" className="relative" aria-label={unreadLabel} onClick={() => navigate("/mijn-praktijk/berichten")}>
                    <MessageCircle className="w-5 h-5" />
                    {unreadMessageCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 bg-destructive text-destructive-foreground text-[9px] font-semibold rounded-full min-w-4 px-0.5 flex items-center justify-center">
                        {unreadMessageCount > 9 ? "9+" : unreadMessageCount}
                      </span>
                    )}
                  </Button>
                  <NotificationBell />
                  <AccountDropdown
                    trigger={
                      <button type="button" aria-label="Accountmenu" className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <NavProfileAvatar preferCompany={effectiveRole === "b2b"} />
                      </button>
                    }
                  />
                </>
              )}
              <button className="p-2" onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="md:hidden py-4 border-t border-border animate-fade-in">
              <div className="flex flex-col gap-1">
                <MobileNav />

                {user && (
                  <>
                    <hr className="border-border my-2" />
                    <MobileUserLinks />
                  </>
                )}

                <hr className="border-border my-2" />

                {user ? (
                  <>
                    {effectiveRole === "client" && (
                      <Link to="/plaats-advertentie" onClick={() => setIsOpen(false)} className="px-4">
                        <Button className="w-full" size="sm">Plaats Hulpvraag</Button>
                      </Link>
                    )}
                    <Button variant="ghost" className="w-full justify-start px-4" onClick={handleSignOut}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Uitloggen
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 px-4">
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full">Inloggen</Button>
                    </Link>
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button className="w-full">Registreren</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ─── Mobile Bottom Navigation Bar ─── */}
      {user && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border md:hidden">
          <div className="flex items-center justify-around h-14">
            <Link
              to="/"
              className={`flex flex-col items-center gap-0.5 text-[10px] p-1 ${location.pathname === "/" ? "text-primary" : "text-muted-foreground"}`}
            >
              <Home className="w-5 h-5" />
              Home
            </Link>
             <Link
              to="/mijn-praktijk/berichten"
              aria-label={unreadLabel}
              className={`flex flex-col items-center gap-0.5 text-[10px] p-1 relative ${location.pathname === "/mijn-praktijk/berichten" ? "text-primary" : "text-muted-foreground"}`}
            >
              <MessageCircle className="w-5 h-5" />
              {unreadMessageCount > 0 && (
                <span className="absolute top-0 right-0 h-4 bg-destructive text-destructive-foreground text-[9px] font-semibold rounded-full min-w-4 px-0.5 flex items-center justify-center">
                  {unreadMessageCount > 9 ? "9+" : unreadMessageCount}
                </span>
              )}
              Berichten
            </Link>
            <Link
              to={effectiveRole === "client" ? "/client-dashboard" : effectiveRole === "b2b" ? "/b2b-dashboard" : effectiveRole === "sales" ? "/sales-dashboard" : "/mijn-praktijk"}
              className={`flex flex-col items-center gap-0.5 text-[10px] p-1 ${
                ["/mijn-praktijk", "/mijn-praktijk/profiel", "/client-dashboard", "/b2b-dashboard", "/sales-dashboard"].includes(location.pathname)
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
            >
              <User className="w-5 h-5" />
              Profiel
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex flex-col items-center gap-0.5 text-[10px] p-1 text-muted-foreground"
            >
              <Menu className="w-5 h-5" />
              Menu
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const Navbar = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user && isPublicPath(pathname)) return <div className="ha ha-fixednav"><HomeNav /></div>;
  return <LegacyNavbar />;
};

export default Navbar;
